
import * as React from "react"
import { toast as sonnerToast, type ToastT } from "sonner";

// Re-export original sonner toast for direct use
export const toast = sonnerToast;

export type ToasterToast = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  variant?: "default" | "destructive";
};

// Create a shadcn-compatible toast function
export const shadcnToast = ({ 
  title, 
  description, 
  variant = "default" 
}: Omit<ToasterToast, "id" | "action">) => {
  // Map variant to sonner properties
  const options: Partial<ToastT> = {};
  
  if (variant === "destructive") {
    return sonnerToast.error(title as string, {
      description: description as string
    });
  }
  
  return sonnerToast(title as string, {
    description: description as string
  });
};

const useToast = () => {
  const [toasts, setToasts] = React.useState<ToasterToast[]>([]);

  const addToast = React.useCallback(
    ({ title, description, action, variant }: Omit<ToasterToast, "id">) => {
      const id = Date.now().toString();
      setToasts((prev) => [...prev, { id, title, description, action, variant }]);
      return id;
    },
    []
  );

  const dismissToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return {
    toasts,
    addToast,
    dismissToast,
    toast: shadcnToast // Add toast function to useToast hook
  };
};

export { useToast };
